import { Controller, useForm } from 'react-hook-form'
import ReactSelect, { components } from 'react-select'
import Switch from 'react-switch'
import isPeselValid from 'pesel-check'
import {
  ClientProfileFragment,
  Gender_Enum,
  useCompletionProfileMutation,
} from '../generated/urql.client'
import { useI18n } from '../provider/i18n'
import { Fragment, useContext, useMemo, useState } from 'react'
import { BiMaleFemale } from 'react-icons/bi'
import { Blue300, Green600 } from '../lib/colors'
import IconInput from './IconInput'
import { MdAspectRatio, MdFlag, MdPerson } from 'react-icons/md'
import { FaAddressCard, FaBaby } from 'react-icons/fa6'
import Countries from '../assets/countries.json'
import { Dialog, Transition } from '@headlessui/react'
import { TailSpin } from 'react-loader-spinner'
import { ClientIdentity } from '../lib/identity'
import { BasketContext } from '../provider/basket'
import messages from '../lib/messages'

type ProfileCompletionFormProps = {
  profile: ClientProfileFragment
}

type FormValues = {
  id: number
  identityType: string
  identityValue: string
  identityCountryCode: string
  identityDateOfBirth: string
  gender: Gender_Enum
}

const ProfileCompletionForm = ({ profile }: ProfileCompletionFormProps) => {
  const basket = useContext(BasketContext)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [{ fetching: isUpdating }, completionProfile] =
    useCompletionProfileMutation()
  const i18n = useI18n()
  const {
    control,
    formState: { errors },
    register,
    handleSubmit,
    watch,
    reset,
    clearErrors,
  } = useForm<FormValues>({
    defaultValues: {
      id: profile.id,
      identityType: profile.identity?.type || 'pesel',
      identityValue: profile.identity?.value || '',
      identityCountryCode: profile.identity?.countryCode || '',
      identityDateOfBirth: profile.identity?.dateOfBirth || '',
      gender: profile.gender || undefined,
    },
  })

  const onModalClose = () => {
    setIsModalOpen(false)
    reset()
  }

  const onModalOpen = () => {
    setIsModalOpen(true)
  }

  const doSaveProfile = async ({
    id,
    identityType,
    identityCountryCode,
    identityDateOfBirth,
    identityValue,
    gender,
  }: FormValues) => {
    let identity: ClientIdentity
    switch (identityType) {
      case 'id_card':
        identity = {
          type: 'id_card',
          value: identityValue,
          countryCode: identityCountryCode,
          dateOfBirth: identityDateOfBirth,
        }
        break
      case 'passport':
        identity = {
          type: 'passport',
          value: identityValue,
          countryCode: identityCountryCode,
          dateOfBirth: identityDateOfBirth,
        }
        break
      case 'pesel':
      default:
        identity = {
          type: 'pesel',
          value: identityValue,
        }
    }

    const { error } = await completionProfile({
      id,
      identity,
      gender,
    })

    if (error) {
      messages.addError(error.message)
      return
    }

    basket.onProfileUpdated({ ...profile, identity, gender })

    onModalClose()
    reset()
  }

  const GenderOptions = useMemo(
    () => [
      {
        value: Gender_Enum.Female,
        label: i18n.t('common.gender.female'),
      },
      {
        value: Gender_Enum.Male,
        label: i18n.t('common.gender.male'),
      },
    ],
    [i18n]
  )

  const IdentityCountryCodeOptions = useMemo(
    () =>
      Countries.map(({ name, flag, isoCode }) => ({
        labelWithFlag: `${flag} ${i18n.t(`common.country.${name}`)}`,
        label: i18n.t(`common.country.${name}`),
        value: isoCode,
        flag,
      })),
    [i18n]
  )

  const IdentityTypeOptions = useMemo(
    () => [
      {
        value: 'id_card',
        label: i18n.t('common.identity.idCard'),
      },
      {
        value: 'passport',
        label: i18n.t('common.identity.passport'),
      },
    ],
    [i18n]
  )

  return (
    <div className="flex gap-4 items-center justify-between w-full">
      <p className="text-base flex gap-4 items-center">
        <MdPerson size={24} color={Blue300} />
        {profile.firstName} {profile.lastName}
      </p>
      <button className="hl-button" onClick={onModalOpen}>
        Uzupełnij
      </button>
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={onModalClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full sm:max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <form onSubmit={handleSubmit(doSaveProfile)}>
                    <div className="flex flex-col gap-4">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-medium leading-6 text-gray-900"
                      >
                        {'Proszę uzupełnić dane profilu'}
                      </Dialog.Title>
                      <Controller
                        name="gender"
                        control={control}
                        rules={{
                          required: i18n.t(
                            'screens.profiles.form.gender.rules.required'
                          ),
                        }}
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <div>
                            <p>
                              {i18n.t(
                                'screens.profiles.form.gender.placeholder'
                              )}
                              :
                            </p>
                            <ReactSelect
                              value={GenderOptions.find(
                                (gender) => gender.value === value
                              )}
                              isSearchable={false}
                              onChange={(option) => onChange(option?.value)}
                              options={GenderOptions}
                              classNames={{
                                input: () => '!p-0 !m-0',
                                control: () =>
                                  '!shadow-none !rounded-none !border-t-0 !border-l-0 !border-r-0 !border-b !border-b-border !border-solid !border-opacity-50 !p-2 !bg-transparent !text-base',
                                valueContainer: () => '!p-0 !pl-7',
                                option: ({ isFocused, isSelected }) =>
                                  `!text-blue-700 ${
                                    isFocused ? '!bg-blue-200' : ''
                                  } ${isSelected ? 'font-bold !bg-white' : ''}`,
                                dropdownIndicator: () => '!text-blue-300 !p-0',
                                indicatorSeparator: () => 'hidden',
                                clearIndicator: () => '!text-blue-300 !p-1',
                              }}
                              placeholder={i18n.t('common.select')}
                              components={{
                                ValueContainer: ({ children, ...props }) => (
                                  <components.ValueContainer {...props}>
                                    <BiMaleFemale
                                      size={24}
                                      color={Blue300}
                                      className="absolute"
                                    />
                                    {children}
                                  </components.ValueContainer>
                                ),
                              }}
                            />
                            {error && (
                              <p className="hl-input-error">{error.message}</p>
                            )}
                          </div>
                        )}
                      />
                      {watch('identityType') === 'pesel' && (
                        <div>
                          <div>
                            {i18n.t('screens.profiles.form.pesel.placeholder')}:
                          </div>
                          <IconInput
                            leftIcon={
                              <MdAspectRatio size={24} color={Blue300} />
                            }
                            {...register('identityValue', {
                              required: i18n.t(
                                'screens.profiles.form.pesel.rules.required'
                              ),
                              validate: (value) =>
                                isPeselValid(value) ||
                                watch('identityType') !== 'pesel'
                                  ? undefined
                                  : i18n.t(
                                      'screens.profiles.form.pesel.rules.pattern'
                                    ),
                            })}
                            placeholder={i18n.t('common.enter')}
                            inputClassName="text-base"
                          />
                          {errors.identityValue && (
                            <p className="hl-input-error">
                              {errors.identityValue.message}
                            </p>
                          )}
                        </div>
                      )}
                      <Controller
                        name="identityType"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <div>
                            <label className="flex gap-4 items-center">
                              <Switch
                                checked={value !== 'pesel'}
                                onChange={(v) => {
                                  onChange(v ? 'id_card' : 'pesel')
                                  clearErrors()
                                }}
                                uncheckedIcon={false}
                                checkedIcon={false}
                                width={40}
                                height={24}
                                onColor={Green600}
                                className="align-middle"
                              />
                              <span className="text-md">
                                {i18n.t('screens.profiles.form.pesel.noPesel')}
                              </span>
                            </label>
                            {errors.identityType && (
                              <p className="hl-input-error">
                                {errors.identityType.message}
                              </p>
                            )}
                          </div>
                        )}
                      />

                      {watch('identityType') !== 'pesel' && (
                        <>
                          <div>
                            <Controller
                              name="identityType"
                              control={control}
                              rules={{
                                required: i18n.t(
                                  'screens.profiles.form.identityType.rules.required'
                                ),
                              }}
                              render={({ field: { value, onChange } }) => (
                                <ReactSelect
                                  value={IdentityTypeOptions.find(
                                    (identityType) =>
                                      identityType.value === value
                                  )}
                                  isSearchable={false}
                                  onChange={onChange}
                                  options={IdentityTypeOptions}
                                  classNames={{
                                    input: () => '!p-0 !m-0',
                                    control: () =>
                                      '!shadow-none !rounded-none !border-t-0 !border-l-0 !border-r-0 !border-b !border-b-border !border-solid !border-opacity-50 !px-2 !py-2.5 !bg-transparent !text-base',
                                    valueContainer: () => '!p-0 !pl-7',
                                    option: ({ isFocused, isSelected }) =>
                                      `!text-blue-700 ${
                                        isFocused ? '!bg-blue-200' : ''
                                      } ${
                                        isSelected ? 'font-bold !bg-white' : ''
                                      }`,
                                    dropdownIndicator: () =>
                                      '!text-blue-300 !p-0',
                                    indicatorSeparator: () => 'hidden',
                                    clearIndicator: () => '!text-blue-300 !p-1',
                                  }}
                                  placeholder={i18n.t(
                                    'screens.profiles.form.identityType.placeholder'
                                  )}
                                  components={{
                                    ValueContainer: ({
                                      children,
                                      ...props
                                    }) => (
                                      <components.ValueContainer {...props}>
                                        <FaAddressCard
                                          size={24}
                                          color={Blue300}
                                          className="absolute"
                                        />
                                        {children}
                                      </components.ValueContainer>
                                    ),
                                  }}
                                />
                              )}
                            />
                          </div>
                          <Controller
                            name="identityCountryCode"
                            control={control}
                            rules={{
                              required: i18n.t(
                                'screens.profiles.form.identityCountryCode.rules.required'
                              ),
                            }}
                            render={({
                              field: { value, onChange },
                              fieldState: { error },
                            }) => (
                              <div>
                                <p>
                                  {i18n.t(
                                    'screens.profiles.form.identityCountryCode.placeholder'
                                  )}
                                  :
                                </p>
                                <ReactSelect
                                  value={IdentityCountryCodeOptions.find(
                                    (identityType) =>
                                      identityType.value === value
                                  )}
                                  onChange={(option) => onChange(option?.value)}
                                  options={IdentityCountryCodeOptions.map(
                                    (option) => ({
                                      ...option,
                                      label: option.labelWithFlag,
                                    })
                                  )}
                                  maxMenuHeight={200}
                                  classNames={{
                                    input: () => '!p-0 !m-0',
                                    control: () =>
                                      '!shadow-none !rounded-none !border-t-0 !border-l-0 !border-r-0 !border-b !border-b-border !border-solid !border-opacity-50 !px-2 !py-2.5 !bg-transparent !text-base',
                                    valueContainer: () => '!p-0 !pl-7',
                                    option: ({ isFocused, isSelected }) =>
                                      `!text-blue-700 ${
                                        isFocused ? '!bg-blue-200' : ''
                                      } ${
                                        isSelected ? 'font-bold !bg-white' : ''
                                      }`,
                                    dropdownIndicator: () =>
                                      '!text-blue-300 !p-0',
                                    indicatorSeparator: () => 'hidden',
                                    clearIndicator: () => '!text-blue-300 !p-1',
                                  }}
                                  placeholder={i18n.t('common.select')}
                                  components={{
                                    ValueContainer: ({
                                      children,
                                      ...props
                                    }) => {
                                      const flag =
                                        IdentityCountryCodeOptions.find(
                                          (option) => option.value === value
                                        )?.flag
                                      return (
                                        <components.ValueContainer {...props}>
                                          {flag ? (
                                            <div className="absolute text-xl">
                                              {flag}
                                            </div>
                                          ) : (
                                            <MdFlag
                                              size={24}
                                              color={Blue300}
                                              className="absolute"
                                            />
                                          )}
                                          {children}
                                        </components.ValueContainer>
                                      )
                                    },
                                  }}
                                />
                                {error && (
                                  <p className="hl-input-error">
                                    {error.message}
                                  </p>
                                )}
                              </div>
                            )}
                          />
                          <div>
                            <p>
                              {i18n.t(
                                'screens.profiles.form.identityValue.placeholder'
                              )}
                              :
                            </p>
                            <IconInput
                              leftIcon={
                                <MdAspectRatio size={24} color={Blue300} />
                              }
                              {...register('identityValue', {
                                required: i18n.t(
                                  'screens.profiles.form.identityValue.rules.required'
                                ),
                              })}
                              placeholder={i18n.t('common.enter')}
                              inputClassName="text-base"
                            />
                            {errors.identityValue && (
                              <p className="hl-input-error">
                                {errors.identityValue.message}
                              </p>
                            )}
                          </div>
                          <div>
                            <p>
                              {i18n.t(
                                'screens.profiles.form.identityDateOfBirth.placeholder'
                              )}
                              :
                            </p>
                            <IconInput
                              leftIcon={<FaBaby size={24} color={Blue300} />}
                              type="date"
                              max={new Date().toISOString()}
                              {...register('identityDateOfBirth', {
                                required: i18n.t(
                                  'screens.profiles.form.identityDateOfBirth.rules.required'
                                ),
                              })}
                              inputClassName="text-base"
                            />
                            {errors.identityDateOfBirth && (
                              <p className="hl-input-error">
                                {errors.identityDateOfBirth.message}
                              </p>
                            )}
                          </div>
                        </>
                      )}

                      <div className="flex flex-row gap-2 justify-end mt-4">
                        <button
                          type="button"
                          className="hl-button hl-gradient-red"
                          onClick={onModalClose}
                          disabled={isUpdating}
                        >
                          {i18n.t('common.cancel')}
                        </button>
                        <button type="submit" className="hl-button">
                          {isUpdating ? (
                            <TailSpin
                              height={24}
                              strokeWidth={3}
                              color="white"
                            />
                          ) : (
                            i18n.t('common.confirm')
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}

export default ProfileCompletionForm
